/**
 * GoogleSignInButton — premium "Continue with Gmail" widget that wraps
 * @react-oauth/google's GoogleLogin component.
 *
 * GMAIL-ONLY-AUTH (2026-02-27)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Props:
 *   • onCredential(credential: string)  — called when Google returns a
 *                                          freshly-signed JWT (ID Token).
 *   • disabled?: boolean
 *
 * Renders Google's native button (signin_with text, large size, pill
 * shape) which delivers a verified ID Token. Callers POST that token
 * to /api/public/auth/google or /api/public/auth/google-register; the
 * backend validates signature + audience + email_verified before
 * issuing a CertOpz session.
 */
import { GoogleLogin } from "@react-oauth/google";
import { useGoogleAuthConfig } from "@/context/GoogleAuthProvider";
import { ShieldCheck } from "lucide-react";

export default function GoogleSignInButton({ onCredential, disabled, label = "signin_with" }) {
  const { enabled, loading } = useGoogleAuthConfig();

  if (loading) {
    return (
      <div className="h-11 rounded-md border border-slate-200 bg-slate-100 animate-pulse" data-testid="google-signin-loading" />
    );
  }
  if (!enabled) {
    return (
      <div
        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-[12px] text-amber-800 leading-snug"
        data-testid="google-signin-disabled"
      >
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-700" />
          <div>
            <b>Google sign-in is not configured yet.</b><br />
            Operator must add a Google Client ID in Platform Settings → Integrations → Google Authentication.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={disabled ? "pointer-events-none opacity-60" : ""} data-testid="google-signin-button">
      <GoogleLogin
        onSuccess={(resp) => {
          if (resp?.credential) onCredential?.(resp.credential);
        }}
        onError={() => { /* swallow — GIS surfaces its own error UI */ }}
        text={label}
        theme="outline"
        size="large"
        shape="pill"
        width="100%"
        useOneTap={false}
      />
    </div>
  );
}
